import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Stack,
  TextField,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'

const ProfileSelector = ({
  profiles,
  selectedProfileIndex,
  isAddingNewProfile,
  onProfileChange,
  onAddProfile,
  onDeleteProfile,
  onCancelAdd,
  onStartAdd,
  onProfileNameChange,
  websiteUrl,
  onWebsiteUrlChange,
  onFetchFromWebsite,
  isFetchingFromWebsite
}) => {
  const { t } = useTranslation()
  const [newProfileName, setNewProfileName] = useState('')

  const handleAddNewProfile = () => {
    if (newProfileName.trim()) {
      onAddProfile(newProfileName.trim())
      setNewProfileName('')
    }
  }

  const handleCancelAdd = () => {
    setNewProfileName('')
    onCancelAdd()
  }

  const handleProfileNameChange = (value) => {
    setNewProfileName(value)
    if (onProfileNameChange) {
      onProfileNameChange(value)
    }
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}
      >
        {t('mailTemplate.users.modal.profileSelector')}
      </Typography>

      {isAddingNewProfile
        ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                autoFocus
                size="small"
                variant="outlined"
                placeholder={t('mailTemplate.users.modal.profileNamePlaceholder')}
                value={newProfileName}
                onChange={(e) => handleProfileNameChange(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newProfileName.trim()) {
                    handleAddNewProfile()
                  } else if (e.key === 'Escape') {
                    handleCancelAdd()
                  }
                }}
                error={!newProfileName.trim()}
                helperText={!newProfileName.trim() ? t('mailTemplate.users.modal.profileNameRequired') : ''}
                sx={{
                  width: 350,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: '14px'
                  }
                }}
              />
              <IconButton
                size="small"
                onClick={handleCancelAdd}
                sx={{
                  mt: 0.5,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                size="small"
                variant="outlined"
                placeholder={t('mailTemplate.users.modal.websiteUrlPlaceholder') || 'Enter website URL (e.g., https://www.example.com)'}
                value={websiteUrl}
                onChange={(e) => onWebsiteUrlChange(e.target.value)}
                sx={{
                  width: 350,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  },
                  '& .MuiFormHelperText-root': {
                    marginTop: '6px',
                    marginLeft: '14px'
                  }
                }}
                helperText={t('mailTemplate.users.modal.websiteUrlHelperText') || 'Optional: Provide a URL to auto-fill profile data'}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={onFetchFromWebsite}
                disabled={!websiteUrl.trim() || isFetchingFromWebsite}
                startIcon={isFetchingFromWebsite ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                sx={{
                  borderRadius: 2,
                  minWidth: '100px',
                  height: '40px'
                }}
              >
                {isFetchingFromWebsite ? '' : t('mailTemplate.users.modal.fetchData') || 'Fetch'}
              </Button>
            </Stack>
          </Stack>
          )
        : (
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ flex: 1, maxWidth: 350 }}>
              <Select
                value={selectedProfileIndex}
                onChange={(e) => onProfileChange(e.target.value)}
                sx={{ borderRadius: 2 }}
                renderValue={(value) => {
                  const profile = profiles[value]
                  return (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {profile?.name}
                    </Typography>
                  )
                }}
              >
                {profiles.map((profile, index) => (
                  <MenuItem key={index} value={index}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {profile.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onStartAdd}
              sx={{ borderRadius: 2 }}
            >
              {t('mailTemplate.users.modal.addProfile')}
            </Button>

            {profiles.length > 1 && (
              <IconButton
                onClick={() => onDeleteProfile(selectedProfileIndex)}
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)'
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
          )}
    </Box>
  )
}

export default ProfileSelector
